import { styled } from "goober";
import type { KeyboardEventHandler, ReactElement, ReactNode, Ref } from "react";
import { Icon, type IconName } from "./Icon";
import { InfoIcon } from "./InfoIcon";
import { PressButton } from "./PressButton";
import { Text } from "./Text";
import { ToastCardContainer } from "./Toast";
import { Tooltip } from "./Tooltip";

const Card = styled(ToastCardContainer)`
  flex-shrink: 0;
  height: 36px;
  background:
    linear-gradient(
      light-dark(transparent, var(--alpha2)),
      light-dark(transparent, var(--alpha2))
    ),
    var(--surface-primary);
`;

const HistoryRow = styled("div")`
  position: relative;
  display: flex;
  flex: 1;
  min-width: 0;
  height: 28px;
  margin: -2px -2px -2px -6px;
  padding: 4px 6px;
`;

const HistoryButton = styled(PressButton)`
  position: absolute;
  inset: 0;
  border-radius: 8px;
  &:hover,
  &[aria-expanded="true"] {
    background: var(--alpha4);
  }
`;

const HistoryContent = styled("div")`
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  pointer-events: none;
`;

const Title = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  container: banner-title / inline-size;
  [data-banner-metadata] {
    display: flex;
    flex-shrink: 0;
  }
  [data-banner-metadata-icon] {
    display: none;
    pointer-events: auto;
  }
  @container banner-title (width < 240px) {
    [data-banner-metadata] {
      display: none;
    }
    [data-banner-metadata-icon] {
      display: inline-flex;
    }
  }
`;

export function BannerTitle({
  name,
  metadata,
}: {
  name: string;
  metadata: string;
}) {
  return (
    <Title>
      <Text variant="text-regular" ellipsis>
        {name}
      </Text>
      <span data-banner-metadata>
        <Text variant="text-small" color="var(--text-secondary)">
          {metadata}
        </Text>
      </span>
      <span data-banner-metadata-icon>
        <Tooltip content={metadata}>
          <InfoIcon icon="clock" />
        </Tooltip>
      </span>
    </Title>
  );
}

export function BranchBanner({
  ref,
  anchorName,
  onKeyDown,
  name,
  icon,
  metadata,
  avatar,
  actions,
  history,
}: {
  ref?: Ref<HTMLDivElement>;
  anchorName?: string;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  name: string;
  icon: IconName | ReactElement;
  metadata: string;
  avatar: ReactNode;
  actions: ReactNode;
  history?: {
    open: boolean;
    onToggle: () => void;
    menu: ReactNode;
  };
}) {
  const contents = (
    <>
      {typeof icon === "string" ? <Icon name={icon} size={16} /> : icon}
      <BannerTitle name={name} metadata={metadata} />
      <div style={{ display: "contents", pointerEvents: "auto" }}>
        {actions}
      </div>
      <div style={{ display: "flex", flexShrink: 0, padding: 4 }}>
        <div style={{ display: "flex", pointerEvents: "auto" }}>{avatar}</div>
      </div>
    </>
  );

  return (
    <Card
      ref={ref}
      role="group"
      aria-label="Branch details"
      style={{ anchorName }}
      onKeyDown={onKeyDown}
    >
      {history ? (
        <HistoryRow>
          <HistoryButton
            data-no-pill
            aria-label={`${name} history`}
            aria-expanded={history.open}
            onPress={history.onToggle}
          >
            {null}
          </HistoryButton>
          <HistoryContent>{contents}</HistoryContent>
        </HistoryRow>
      ) : (
        contents
      )}
      {history?.menu}
    </Card>
  );
}
